import { useEffect, useRef } from 'react';
import { MessageBubble } from './MessageBubble';
import { EmptyState } from './EmptyState';
import { Loader2 } from 'lucide-react';
import { MessageDto, MessagesQuery } from '../../types';
import { useMessages } from '../../hooks/useConversations';

interface MessageListProps {
  threadId: string | undefined;
  options?: MessagesQuery;
}

export function MessageList({ threadId, options }: MessageListProps) {
  const {
    data,
    fetchNextPage,
    hasNextPage,
    isFetching,
    isFetchingNextPage,
    isLoading,
  } = useMessages(threadId, options);

  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const observerTarget = useRef<HTMLDivElement>(null);

  // Infinite scroll with Intersection Observer
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasNextPage && !isFetchingNextPage) {
          fetchNextPage();
        }
      },
      { threshold: 1.0 },
    );

    if (observerTarget.current) {
      observer.observe(observerTarget.current);
    }

    return () => observer.disconnect();
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  // Flatten pages into single array
  const messages = data?.pages.flatMap((page) => page.items) ?? [];

  // Auto-scroll to bottom on initial load
  useEffect(() => {
    if (scrollContainerRef.current && messages.length > 0 && !isFetching) {
      scrollContainerRef.current.scrollTop =
        scrollContainerRef.current.scrollHeight;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [threadId]); // Only on threadId change - intentionally excluding isFetching and messages.length

  if (!threadId) {
    return (
      <div className="flex items-center justify-center h-full">
        <EmptyState message="Selecione uma conversa para visualizar as mensagens" />
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-full">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (messages.length === 0) {
    return (
      <div className="flex items-center justify-center h-full">
        <EmptyState message="Nenhuma mensagem nesta conversa" />
      </div>
    );
  }

  return (
    <div
      ref={scrollContainerRef}
      className="flex-1 overflow-y-auto px-4 py-6 space-y-2"
    >
      {/* Loading indicator at top (when loading older messages) */}
      {isFetchingNextPage && (
        <div className="flex items-center justify-center py-4">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </div>
      )}

      {/* Intersection observer target at top (for loading older messages) */}
      <div ref={observerTarget} className="h-4" />

      {/* Messages */}
      {messages.map((message: MessageDto) => (
        <MessageBubble key={message.id} message={message} />
      ))}

      {/* No more data indicator */}
      {!hasNextPage && messages.length > 0 && (
        <div className="text-center text-sm text-muted-foreground py-4">
          Início da conversa
        </div>
      )}
    </div>
  );
}
